import { Slot } from '@radix-ui/react-slot'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@repo/ui/alert-dialog'
import { useT } from '@repo/ui/hooks'
import { cn } from '@repo/ui/utils'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'
import { useState } from 'react'

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 dark:ring-offset-primary-950 dark:focus-visible:ring-primary-300 select-none gap-2',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-50 hover:bg-primary/90 dark:bg-primary-50 dark:text-primary-900 dark:hover:bg-primary-50/90',
        highlight:
          'bg-highlight text-primary-50 hover:bg-highlight/90 dark:bg-primary-50 dark:text-primary-900 dark:hover:bg-primary-50/90',
        destructive:
          'bg-red-500 text-primary-50 hover:bg-red-500/90 dark:bg-red-900 dark:text-primary-50 dark:hover:bg-red-900/90',
        outline:
          'text-primary border border-primary-200 bg-white hover:bg-primary-100 hover:text-primary-900 dark:border-primary-800 dark:bg-primary-950 dark:hover:bg-primary-800 dark:hover:text-primary-50',
        secondary:
          'bg-primary-100 text-primary-900 hover:bg-primary-100/80 dark:bg-primary-800 dark:text-primary-50 dark:hover:bg-primary-800/80 border-primary-200 border',
        ghost:
          'hover:bg-primary-100 hover:text-primary-600 dark:hover:bg-primary-800 dark:hover:text-primary-50',
        link: 'text-primary-900 underline-offset-4 hover:underline dark:text-primary-50',
      },
      size: {
        default: 'h-10 px-4 py-2',
        xs: 'h-6 rounded-md px-1',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export type ButtonVariant = VariantProps<typeof buttonVariants>['variant']
export type SensitiveVariant = 'destructive' | 'secondary' | 'primary'

export interface SensitiveOptions {
  title?: string
  description?: string
  variant?: SensitiveVariant
}

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  isSensitive?: boolean | SensitiveOptions
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
}

const sensitiveVariantStyles: Record<SensitiveVariant, string> = {
  destructive: 'transition-colors hover:border-red-200 hover:bg-red-100 hover:text-red-500',
  secondary:
    'transition-colors hover:border-primary-200 hover:bg-primary-100/80 hover:text-primary-900',
  primary: 'transition-colors hover:border-primary/90 hover:bg-primary/90 hover:text-primary-50',
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, isSensitive = false, onClick, ...props }, ref) => {
    const { tc, t } = useT({ keyPrefix: 'base.button' })
    const [showConfirm, setShowConfirm] = useState(false)
    const Comp = asChild ? Slot : 'button'

    // Determine if sensitive and extract options
    const isSensitiveObj = typeof isSensitive === 'object' && isSensitive !== null
    const sensitiveVariant: SensitiveVariant =
      isSensitiveObj && isSensitive.variant ? isSensitive.variant : 'destructive'
    const sensitiveTitle =
      isSensitiveObj && isSensitive.title ? isSensitive.title : t('confirm.title')
    const sensitiveDescription =
      isSensitiveObj && isSensitive.description ? isSensitive.description : t('confirm.description')
    const isSensitiveActive = Boolean(isSensitive)

    const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (isSensitiveActive) {
        e.preventDefault()
        e.stopPropagation()
        setShowConfirm(true)
      } else {
        onClick?.(e)
      }
    }

    return (
      <>
        <Comp
          className={cn(
            buttonVariants({ variant, size, className }),
            isSensitiveActive && variant === 'outline' && sensitiveVariantStyles[sensitiveVariant],
          )}
          ref={ref}
          type={props.type || 'button'}
          {...props}
          onClick={handleClick}
        />
        {isSensitiveActive && (
          <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{sensitiveTitle}</AlertDialogTitle>
                <AlertDialogDescription>{sensitiveDescription}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{tc('cancel')}</AlertDialogCancel>
                <AlertDialogAction onClick={onClick}>{tc('continue')}</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </>
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
